import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useGetRatingResponseFullObject } from '../hooks/reactQuerys/RatingFormQueries';

interface IProps {
  idRatingResponse: number | null;
  onClose: () => void;
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString('de-DE') : '–';

const StarsView: React.FC<{ value: number; max?: number }> = ({
  value,
  max = 5,
}) => {
  const items = Array.from({ length: max }, (_, i) => i + 1);
  return (
    <Stack direction='row' spacing={0.25}>
      {items.map(n =>
        n <= value ? (
          <StarIcon key={n} fontSize='small' color='warning' />
        ) : (
          <StarOutlineIcon key={n} fontSize='small' color='disabled' />
        )
      )}
      <Typography variant='caption' sx={{ ml: 1, alignSelf: 'center' }}>
        {value}/{max}
      </Typography>
    </Stack>
  );
};

export const RatingResponseDetailDialog: React.FC<IProps> = ({
  idRatingResponse,
  onClose,
}) => {
  const theme = useTheme();
  const open = idRatingResponse != null;
  const { response, isLoadingResponse } =
    useGetRatingResponseFullObject(idRatingResponse);

  const recipient = response
    ? [response.FirstName, response.LastName].filter(Boolean).join(' ') ||
      response.Email ||
      `Mandant #${response.idLegalClient}`
    : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Box>
            <Typography variant='h6' component='div'>
              Bewertungsantwort
            </Typography>
            {response && (
              <Typography variant='caption' color='text.secondary'>
                {response.RatingFormTitle ?? '—'} · {recipient}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label='Schließen'>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoadingResponse || !response ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Übersicht
              </Typography>
              <Table size='small'>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 200 }} component='th'>
                      Status
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        color={
                          response.Status === 'submitted'
                            ? 'success'
                            : response.Status === 'failed'
                              ? 'error'
                              : 'default'
                        }
                        label={response.Status ?? '—'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Versendet</TableCell>
                    <TableCell>{formatDate(response.SentAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Geöffnet</TableCell>
                    <TableCell>{formatDate(response.OpenedAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Abgegeben</TableCell>
                    <TableCell>{formatDate(response.SubmittedAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component='th'>Gesamtbewertung</TableCell>
                    <TableCell>
                      {response.OverallScore != null ? (
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <StarsView
                            value={Math.round(Number(response.OverallScore))}
                          />
                          <Typography variant='body2'>
                            ({Number(response.OverallScore).toFixed(2)})
                          </Typography>
                        </Stack>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                  </TableRow>
                  {response.MailErrorMessage && (
                    <TableRow>
                      <TableCell component='th'>Mail-Fehler</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>
                        {response.MailErrorMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Divider />

            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Antworten
              </Typography>
              {response.Answers.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  Es liegen noch keine Einzelantworten vor.
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Frage</TableCell>
                      <TableCell sx={{ width: 200 }}>Antwort</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response.Answers.map(a => (
                      <TableRow key={a.idRatingAnswer}>
                        <TableCell>
                          {a.QuestionLabel ??
                            `Frage #${a.idRatingFormQuestion}`}
                        </TableCell>
                        <TableCell>
                          {a.QuestionType === 'stars' &&
                          a.AnswerNumeric != null ? (
                            <StarsView
                              value={Number(a.AnswerNumeric)}
                              max={5}
                            />
                          ) : a.QuestionType === 'scale' &&
                            a.AnswerNumeric != null ? (
                            <Chip
                              size='small'
                              label={String(a.AnswerNumeric)}
                              sx={{
                                backgroundColor: theme.palette.action.selected,
                              }}
                            />
                          ) : a.QuestionType === 'yesno' &&
                            a.AnswerNumeric != null ? (
                            <Chip
                              size='small'
                              color={
                                Number(a.AnswerNumeric) === 1
                                  ? 'success'
                                  : 'default'
                              }
                              label={
                                Number(a.AnswerNumeric) === 1 ? 'Ja' : 'Nein'
                              }
                            />
                          ) : a.AnswerText ? (
                            <Typography
                              variant='body2'
                              sx={{ whiteSpace: 'pre-wrap' }}
                            >
                              {a.AnswerText}
                            </Typography>
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Typography variant='caption' color='text.secondary'>
                Token: {response.Token}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
